/**
 * Geauxjüre Reader — player icon set.
 *
 * Solid silhouettes, single hand. 24×24 viewBox, currentColor.
 * Designed to read at 18×18, the smallest size used in the dock.
 *
 * Author: Claude Design pass, 2026-05-15. Locked decisions documented
 * in docs/notes/reader-player-design.md (Approved Decisions).
 */

import type { JSX } from 'preact';

type Props = JSX.SVGAttributes<SVGSVGElement> & { size?: number };

function svgProps({ size = 24, ...rest }: Props): JSX.SVGAttributes<SVGSVGElement> {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    'aria-hidden': 'true',
    focusable: 'false',
    ...rest,
  };
}

// ── Play ──────────────────────────────────────────────────
// Filled triangle, no expanding stroke. Visual weight is the fill
// silhouette — matches the thin ring around the button.
export function PlayIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <path
        d="M9.0 6.0 L9.0 18.0 L18.4 12 Z"
        fill="currentColor"
      />
    </svg>
  );
}

// ── Pause ─────────────────────────────────────────────────
// Thinner bars to read at the same weight as the play triangle.
export function PauseIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <rect x="7.6" y="6" width="2.6" height="12" rx="0.6" fill="currentColor" />
      <rect x="13.8" y="6" width="2.6" height="12" rx="0.6" fill="currentColor" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════
// Player-bar transport glyphs
//
// Five-button transport row: skip-prev · scrub-bck · play · scrub-fwd ·
// skip-next. Icon-only at every size (no numbers, no labels). Sizing is
// handled by `.player-bar` em-scaling in CSS; these SVGs stay on the
// 24×24 viewBox the rest of the icon set uses, so `size` continues to
// behave the same way.
//
// Scrub: open ring + chunky arrowhead. The step value (currently 60s)
// lives in the aria-label / title attribute, not in the glyph.
// Skip: chunky triangle + vertical bar pair, mirrored for prev/next.
// ═══════════════════════════════════════════════════════════

// ── Scrub back (↺) ────────────────────────────────────────
// 270° arc from 12 o'clock CW around the right side, bottom, and
// left side to 9 o'clock. The upper-left quadrant (9 → 12) is open,
// with a prominent triangular arrowhead AT 12 o'clock pointing
// LEFT — its base sits on the arc start, apex extends into the gap.
export function ScrubBackIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <path
        d="M 12 4.5 A 7.5 7.5 0 1 1 4.5 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.1"
        stroke-linecap="round"
      />
      <path
        d="M 7.5 4.5 L 12 2.6 L 12 6.4 Z"
        fill="currentColor"
      />
    </svg>
  );
}

// ── Scrub forward (↻) ─────────────────────────────────────
// Mirror: 270° arc from 12 o'clock CCW around the left side, bottom,
// and right side to 3 o'clock. Upper-right quadrant (12 → 3) is
// open, arrowhead AT 12 o'clock pointing RIGHT.
export function ScrubFwdIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <path
        d="M 12 4.5 A 7.5 7.5 0 1 0 19.5 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.1"
        stroke-linecap="round"
      />
      <path
        d="M 16.5 4.5 L 12 2.6 L 12 6.4 Z"
        fill="currentColor"
      />
    </svg>
  );
}

// ── Skip previous track / chapter (|◀) ────────────────────
// Thin bar + smaller triangle, shoulder-to-shoulder, no gap.
// Sized to match the lightened play-button visual weight.
export function SkipPrevIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g fill="currentColor">
        <rect x="6.0" y="7.0" width="1.4" height="10" rx="0.4" />
        <path d="M 17.4 7.0 L 17.4 17.0 L 8.0 12 Z" />
      </g>
    </svg>
  );
}

// ── Skip next track / chapter (▶|) ────────────────────────
// Triangle + thin bar, shoulder-to-shoulder, no gap.
export function SkipNextIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g fill="currentColor">
        <path d="M 6.6 7.0 L 6.6 17.0 L 16.0 12 Z" />
        <rect x="16.6" y="7.0" width="1.4" height="10" rx="0.4" />
      </g>
    </svg>
  );
}

// ── Gear ──────────────────────────────────────────────────
// 6 trapezoidal teeth + circular body + circular centre hole.
export function GearIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g
        fill="none"
        stroke="currentColor"
        stroke-width="1.1"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z" />
        <circle cx="12" cy="12" r="3" />
      </g>
    </svg>
  );
}

// ── AI sparkle ────────────────────────────────────────────
// Hairline circle ring (same treatment as play) containing two filled
// 4-point stars — larger upper-left, smaller lower-right. Concave sides
// (not straight diamond) for a true twinkle silhouette.
export function AiSparkleIcon(props: Props) {
  const star = (cx: number, cy: number, r: number, w = 0.18): string => {
    const c = r * w;
    return `
      M ${cx} ${cy - r}
      C ${cx + c} ${cy - c}, ${cx + c} ${cy - c}, ${cx + r} ${cy}
      C ${cx + c} ${cy + c}, ${cx + c} ${cy + c}, ${cx} ${cy + r}
      C ${cx - c} ${cy + c}, ${cx - c} ${cy + c}, ${cx - r} ${cy}
      C ${cx - c} ${cy - c}, ${cx - c} ${cy - c}, ${cx} ${cy - r}
      Z`;
  };
  return (
    <svg {...svgProps(props)}>
      <circle cx="12" cy="12" r="10.2" fill="none" stroke="currentColor" stroke-width="1.1" />
      <path d={star(9.6, 10.0, 4.0, 0.16)} fill="currentColor" />
      <path d={star(16.0, 15.6, 2.1, 0.18)} fill="currentColor" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════
// Share tray glyphs — monochrome, currentColor, no licensed marks.
// ═══════════════════════════════════════════════════════════

export function WhatsAppIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <path
        fill="currentColor"
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M 12 2.4 a 9.6 9.6 0 0 0 -8.42 14.2 L 2.4 21.6 L 7.18 20.2 A 9.6 9.6 0 1 0 12 2.4 Z M 8.9 7.0 a 1.0 1.0 0 0 1 0.94 0.66 l 0.74 2.0 a 1.0 1.0 0 0 1 -0.24 1.04 l -0.72 0.72 c 0.72 1.4, 1.86 2.54, 3.26 3.26 l 0.72 -0.72 a 1.0 1.0 0 0 1 1.04 -0.24 l 2.0 0.74 a 1.0 1.0 0 0 1 0.66 0.94 v 0.94 a 1.6 1.6 0 0 1 -1.7 1.6 c -4.46 -0.18, -8.22 -3.94, -8.4 -8.4 a 1.6 1.6 0 0 1 1.6 -1.7 Z"
      />
    </svg>
  );
}

export function TelegramIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <path
        fill="currentColor"
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M 21.8 2.4 L 2.4 10.0 L 8.6 12.4 L 11.2 20.6 L 14.2 15.0 L 19.4 18.0 Z M 8.6 12.4 L 17.4 5.8 L 11.2 13.0 L 11.2 15.6 Z"
      />
    </svg>
  );
}

export function EmailIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g
        fill="none"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect x="3" y="5" width="18" height="14" rx="2.4" />
        <path d="M 3.5 7.2 L 12 13 L 20.5 7.2" />
      </g>
    </svg>
  );
}

// Two overlapping chat bubbles. Front bubble has a hairline stroke
// in surface to read as two stacked, not one.
export function SmsIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g fill="currentColor">
        <path d="M 4.6 2.4 H 14.4 a 2.4 2.4 0 0 1 2.4 2.4 V 10.6 a 2.4 2.4 0 0 1 -2.4 2.4 H 8.0 L 4.4 16.4 L 5.0 13.0 a 2.4 2.4 0 0 1 -2.4 -2.4 V 4.8 a 2.4 2.4 0 0 1 2.0 -2.4 Z" />
        <path
          stroke="var(--surface, #fbf8f2)"
          stroke-width="1.2"
          stroke-linejoin="round"
          d="M 9.6 8.4 H 19.4 a 2.4 2.4 0 0 1 2.4 2.4 V 14.6 a 2.4 2.4 0 0 1 -2.4 2.4 H 16.0 L 19.6 22.0 L 14.4 19.0 H 9.6 a 2.4 2.4 0 0 1 -2.4 -2.4 V 10.8 a 2.4 2.4 0 0 1 2.4 -2.4 Z"
        />
      </g>
    </svg>
  );
}

export function CopyLinkIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g
        fill="none"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M 10.5 13.5 a 4 4 0 0 1 0 -5.66 l 2.83 -2.83 a 4 4 0 0 1 5.66 5.66 l -1.06 1.06" />
        <path d="M 13.5 10.5 a 4 4 0 0 1 0 5.66 l -2.83 2.83 a 4 4 0 0 1 -5.66 -5.66 l 1.06 -1.06" />
      </g>
    </svg>
  );
}

export function CloseIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <path
        d="M 6 6 L 18 18 M 18 6 L 6 18"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
      />
    </svg>
  );
}

export function RefreshIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M20 5 L20 10 L15 10" />
        <path d="M20 10 A 8 8 0 1 0 17.9 17.5" />
      </g>
    </svg>
  );
}

export function SearchIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="11" cy="11" r="6.5" />
        <path d="M 16 16 L 20.5 20.5" />
      </g>
    </svg>
  );
}

export function BackArrowIcon(props: Props) {
  return (
    <svg {...svgProps(props)}>
      <g
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M 10 6 L 4.5 12 L 10 18" />
        <path d="M 4.6 12 H 19.5" />
      </g>
    </svg>
  );
}

export function QrCodeIcon(props: Props) {
  return (
    <svg {...svgProps(props)} fill="currentColor">
      {/* top-left finder pattern */}
      <path d="M3 3h8v8H3V3zm1.5 1.5v5h5v-5h-5zM6 6h2v2H6V6z" />
      {/* top-right finder pattern */}
      <path d="M13 3h8v8h-8V3zm1.5 1.5v5h5v-5h-5zM16 6h2v2h-2V6z" />
      {/* bottom-left finder pattern */}
      <path d="M3 13h8v8H3v-8zm1.5 1.5v5h5v-5h-5zM6 16h2v2H6v-2z" />
      {/* bottom-right data modules */}
      <rect x="13" y="13" width="2" height="2" />
      <rect x="17" y="13" width="4" height="2" />
      <rect x="13" y="16" width="2" height="2" />
      <rect x="16" y="16" width="2" height="2" />
      <rect x="19" y="16" width="2" height="2" />
      <rect x="13" y="19" width="4" height="2" />
      <rect x="19" y="19" width="2" height="2" />
    </svg>
  );
}
